import {
  collection,
  collectionGroup,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from './firebase';
import { adminService, type AdminUser, type UserWorkspaceProfile } from './admin';
import { adminBillingService } from './adminBilling';
import type { PlatformSubscription } from './billing/types';
import type { ProfileData } from './onboardingProfile';
import { getProfilePhotoURL } from './profilePhoto';
import { safeParseDate } from './utils';
import { format } from 'date-fns';

export type UserAccountStatus = 'active' | 'suspended' | 'banned';

export type AdminUserListItem = AdminUser & {
  accountStatus: UserAccountStatus;
  onboarded: boolean;
  loginCount: number;
  planTier: string;
  lastLoginAt?: string;
};

export type AdminAuditEntry = {
  at: string;
  action: string;
  by?: string;
  detail?: string;
};

export type LegalAcceptanceRecord = {
  id: string;
  userId: string;
  type: string;
  version: string;
  email?: string;
  displayName?: string;
  acceptedAt: string;
  ipAddress?: string;
  userAgent?: string;
  clientMetadata?: Record<string, string>;
  documentHash?: string;
  requestId?: string;
};

export type UserAdminDetail = {
  user: AdminUserListItem;
  workspaces: UserWorkspaceProfile[];
  usage: {
    workspaceCount: number;
    documentCount: number;
    caseCount: number;
    loginCount: number;
    aiCredits: number;
    aiCreditsUsed: number;
    aiDraftsThisMonth: number;
    documentsThisMonth: number;
  };
  billing: {
    planTier: string;
    subscription: Record<string, unknown> | null;
    platformSubscription: PlatformSubscription | null;
    status: string;
    currentPeriodEnd?: string;
  };
  profileData: ProfileData | null;
  photoURL: string | null;
  recentDocuments: {
    id: string;
    workspaceId: string;
    title: string;
    type?: string;
    updatedAt?: string;
  }[];
  loginHistory: { label: string; at: string }[];
  timeline: AdminAuditEntry[];
  legalAcceptanceRecords: LegalAcceptanceRecord[];
};

const LIST_LIMIT = 1000;

async function fetchLegalAcceptanceRecords(userId: string): Promise<LegalAcceptanceRecord[]> {
  try {
    const snap = await getDocs(
      query(collection(db, 'legal_acceptance_records'), where('userId', '==', userId)),
    );
    return snap.docs
      .map((d) => ({ id: d.id, ...(d.data() as Omit<LegalAcceptanceRecord, 'id'>) }))
      .filter((r) =>
        r.type === 'ip_protection_agreement' ||
        r.type === 'intellectual_property_notice' ||
        r.type === 'acknowledgement' ||
        r.type === 'affidavit',
      )
      .sort(
        (a, b) =>
          (safeParseDate(b.acceptedAt)?.getTime() ?? 0) -
          (safeParseDate(a.acceptedAt)?.getTime() ?? 0),
      );
  } catch (e) {
    console.warn('[adminUsers] legal acceptance records', e);
    return [];
  }
}

export function normalizeAccountStatus(raw?: string): UserAccountStatus {
  if (raw === 'banned') return 'banned';
  if (raw === 'suspended') return 'suspended';
  return 'active';
}

export function mapUserDoc(id: string, data: Record<string, unknown>): AdminUserListItem {
  const sub = data.subscription as Record<string, unknown> | undefined;
  const planTier = String(
    data.planTier ?? sub?.plan ?? 'free',
  ).toLowerCase();

  return {
    id,
    email: String(data.email ?? ''),
    name: data.name as string | undefined,
    displayName: data.displayName as string | undefined,
    role: data.role as string | undefined,
    systemStatus: data.systemStatus as string | undefined,
    createdAt: data.createdAt as string | undefined,
    accountStatus: normalizeAccountStatus(data.systemStatus as string | undefined),
    onboarded: Boolean(data.onboarded),
    loginCount: Number(data.loginCount ?? 0),
    planTier,
    lastLoginAt: data.lastLoginAt as string | undefined,
    aiCredits: Number(data.aiCredits ?? 0),
    aiCreditsUsed: Number(data.aiCreditsUsed ?? 0),
    forceLogoutAt: data.forceLogoutAt as string | undefined,
    adminAuditLog: (data.adminAuditLog as AdminAuditEntry[]) ?? [],
    ...data,
  };
}

export const adminUsersService = {
  async listUsers(): Promise<AdminUserListItem[]> {
    try {
      const snap = await getDocs(query(collection(db, 'users'), limit(LIST_LIMIT)));
      return snap.docs.map((d) => mapUserDoc(d.id, d.data() as Record<string, unknown>));
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'users');
      return [];
    }
  },

  async getUserDetail(userId: string): Promise<UserAdminDetail | null> {
    try {
      const { user, workspaces } = await adminService.getAdminUserProfile(userId);
      const userSnap = await getDoc(doc(db, 'users', userId));
      if (!userSnap.exists()) return null;

      const mapped = mapUserDoc(userSnap.id, userSnap.data() as Record<string, unknown>);

      let documentCount = 0;
      let caseCount = 0;

      try {
        const docQ = query(
          collectionGroup(db, 'documents'),
          where('lawyerId', '==', userId),
        );
        documentCount = (await getCountFromServer(docQ)).data().count;
      } catch (e) {
        console.warn('[adminUsers] document count', e);
      }

      try {
        const caseQ = query(collectionGroup(db, 'cases'), where('lawyerId', '==', userId));
        caseCount = (await getCountFromServer(caseQ)).data().count;
      } catch (e) {
        console.warn('[adminUsers] case count', e);
      }

      let recentDocuments: UserAdminDetail['recentDocuments'] = [];
      try {
        const recentQ = query(
          collectionGroup(db, 'documents'),
          where('lawyerId', '==', userId),
          orderBy('updatedAt', 'desc'),
          limit(15),
        );
        const recentSnap = await getDocs(recentQ);
        recentDocuments = recentSnap.docs.map((d) => {
          const data = d.data();
          const path = d.ref.path.split('/');
          const workspaceId = path.length >= 2 ? path[1] : '';
          return {
            id: d.id,
            workspaceId,
            title: String(data.title ?? data.name ?? 'Untitled'),
            type: data.type as string | undefined,
            updatedAt: data.updatedAt as string | undefined,
          };
        });
      } catch (e) {
        console.warn('[adminUsers] recent documents', e);
      }

      const sub = (mapped.subscription as Record<string, unknown> | undefined) ?? null;
      const audit = (mapped.adminAuditLog as AdminAuditEntry[]) ?? [];

      const loginHistory: UserAdminDetail['loginHistory'] = [];
      if (mapped.createdAt) {
        loginHistory.push({ label: 'Account created', at: mapped.createdAt });
      }
      if (mapped.lastLoginAt) {
        loginHistory.push({ label: 'Last login', at: mapped.lastLoginAt });
      }
      loginHistory.push({
        label: `Total logins recorded`,
        at: mapped.lastLoginAt ?? mapped.createdAt ?? new Date().toISOString(),
      });

      const timeline = buildTimeline(mapped, audit, workspaces);
      const legalAcceptanceRecords = await fetchLegalAcceptanceRecords(userId);

      const period = format(new Date(), 'yyyy-MM');
      const usagePeriod = (userSnap.data().usagePeriod as
        | { period?: string; aiDrafts?: number; documentsCreated?: number }
        | undefined);
      const inPeriod = usagePeriod?.period === period;

      const rawProfile = userSnap.data().profileData as ProfileData | undefined;
      const profileData = rawProfile && Object.keys(rawProfile).length > 0 ? rawProfile : null;
      const photoURL = getProfilePhotoURL(null, userSnap.data());

      const platformSubscription = await adminBillingService.getSubscription(userId);
      const billingStatus = platformSubscription?.status ?? 'active';

      return {
        user: { ...mapped, ...user, id: userId } as AdminUserListItem,
        workspaces,
        usage: {
          workspaceCount: workspaces.length,
          documentCount,
          caseCount,
          loginCount: mapped.loginCount,
          aiCredits: Number(mapped.aiCredits ?? 0),
          aiCreditsUsed: Number(mapped.aiCreditsUsed ?? 0),
          aiDraftsThisMonth: inPeriod ? Number(usagePeriod?.aiDrafts ?? 0) : 0,
          documentsThisMonth: inPeriod ? Number(usagePeriod?.documentsCreated ?? 0) : 0,
        },
        billing: {
          planTier: mapped.planTier,
          subscription: sub,
          platformSubscription,
          status: billingStatus,
          currentPeriodEnd: platformSubscription?.currentPeriodEnd,
        },
        profileData,
        photoURL,
        recentDocuments,
        loginHistory: loginHistory.sort(
          (a, b) =>
            (safeParseDate(b.at)?.getTime() ?? 0) - (safeParseDate(a.at)?.getTime() ?? 0),
        ),
        timeline,
        legalAcceptanceRecords,
      };
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, `users/${userId}`);
      return null;
    }
  },
};

function buildTimeline(
  user: AdminUserListItem,
  audit: AdminAuditEntry[],
  workspaces: UserWorkspaceProfile[],
): AdminAuditEntry[] {
  const events: AdminAuditEntry[] = [...audit];

  if (user.createdAt) {
    events.push({ at: user.createdAt, action: 'Account registered' });
  }
  if (user.onboarded) {
    events.push({
      at: user.lastLoginAt ?? user.createdAt ?? new Date().toISOString(),
      action: 'Completed onboarding',
    });
  }
  const termsAt = user.termsAcceptedAt as string | undefined;
  const termsVersion = user.termsVersion as string | undefined;
  if (termsAt) {
    events.push({
      at: termsAt,
      action: 'Accepted Terms & Conditions',
      detail: termsVersion ? `v${termsVersion}` : undefined,
    });
  }
  const affidavitAt = user.affidavitAcceptedAt as string | undefined;
  const affidavitVersion = user.affidavitVersion as string | undefined;
  if (affidavitAt) {
    events.push({
      at: affidavitAt,
      action: 'Signed Intellectual Property Protection Agreement',
      detail: affidavitVersion ? `v${affidavitVersion}` : undefined,
    });
  }
  for (const ws of workspaces.slice(0, 5)) {
    if (ws.joinedAt) {
      events.push({
        at: ws.joinedAt,
        action: `Joined workspace`,
        detail: ws.workspaceName,
      });
    }
  }

  return events
    .filter((e) => e.at)
    .sort(
      (a, b) =>
        (safeParseDate(b.at)?.getTime() ?? 0) - (safeParseDate(a.at)?.getTime() ?? 0),
    )
    .slice(0, 40);
}
